import { observer } from "mobx-react-lite";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import React, { useContext, useEffect, useState } from "react";
import { RootStoreContext } from "../../../../../app/stores/rootStore";
import agent from "../../../../../app/api/agent";

const ProjectMembers = ({ projectId }) => {
  const rootStore = useContext(RootStoreContext);
  const { projectMemberStore } = rootStore;
  const { members, loading, fetchMembers, addMember, removeMember } =
    projectMemberStore;

  const [displayAddDialog, setDisplayAddDialog] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("viewer");
  const [userOptions, setUserOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const roleOptions = [
    { label: "Manager", value: "manager" },
    { label: "Contributor", value: "contributor" },
    { label: "Voter", value: "voter" },
    { label: "Viewer", value: "viewer" },
  ];

  useEffect(() => {
    if (projectId) {
      fetchMembers(projectId);
    }
  }, [projectId, fetchMembers]);

  const openAddDialog = async () => {
    setDisplayAddDialog(true);
    setNewEmail("");
    setNewRole("viewer");
    setLoadingUsers(true);
    try {
      const users = await agent.Admin.userList();
      const existingEmails = new Set(members.map(m => m.userEmail));
      setUserOptions(
        (users || [])
          .filter(u => !existingEmails.has(u.email || u.Email))
          .map(u => ({ label: `${u.email || u.Email}${u.displayName || u.DisplayName ? ` (${u.displayName || u.DisplayName})` : ""}`, value: u.email || u.Email }))
      );
    } catch {
      setUserOptions([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAdd = () => {
    if (!newEmail.trim()) return;
    addMember(projectId, newEmail.trim(), newRole);
    setNewEmail("");
    setNewRole("viewer");
    setDisplayAddDialog(false);
  };

  const handleRemove = (email) => {
    removeMember(projectId, email);
  };

  const roleBodyTemplate = (rowData) => {
    const severityMap = {
      manager: "warning",
      voter: "success",
      viewer: "info",
      contributor: "help",
    };
    return (
      <Tag
        value={rowData.role}
        severity={severityMap[rowData.role] || "info"}
      />
    );
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-danger p-button-text"
        tooltip="Remove member"
        onClick={() => handleRemove(rowData.userEmail)}
      />
    );
  };

  return (
    <div className="flex flex-column w-full">
      <div className="flex w-full justify-content-end mb-2">
        <Button
          label="Add Member"
          icon="pi pi-plus"
          className="p-button-sm"
          onClick={() => openAddDialog()}
        />
      </div>

      <DataTable
        value={members}
        loading={loading}
        className="w-full"
        emptyMessage="No members found."
        responsiveLayout="scroll"
      >
        <Column
          field="userEmail"
          header="Email"
          sortable
          style={{ width: "60%" }}
        />
        <Column
          field="role"
          header="Role"
          body={roleBodyTemplate}
          sortable
          style={{ width: "20%" }}
        />
        <Column
          body={actionBodyTemplate}
          style={{ width: "20%" }}
          header="Actions"
        />
      </DataTable>

      <Dialog
        header="Add Project Member"
        visible={displayAddDialog}
        onHide={() => setDisplayAddDialog(false)}
        style={{ width: "400px" }}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setDisplayAddDialog(false)}
            />
            <Button
              label="Add"
              icon="pi pi-check"
              onClick={handleAdd}
              autoFocus
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3 p-3">
          <div className="flex flex-column gap-1">
            <label htmlFor="user">Select User</label>
            <Dropdown
              id="user"
              value={newEmail}
              options={userOptions}
              onChange={(e) => setNewEmail(e.value)}
              placeholder={loadingUsers ? "Loading users..." : "Select a user..."}
              filter
              filterBy="label"
              style={{ width: "100%" }}
            />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="role">Role</label>
            <Dropdown
              id="role"
              value={newRole}
              options={roleOptions}
              onChange={(e) => setNewRole(e.value)}
              className="w-full"
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default observer(ProjectMembers);
